import { useEffect } from 'react'

import type {
  GetStaticPathsContext,
  GetStaticPropsContext,
  InferGetStaticPropsType,
} from 'next'
import { useRouter } from 'next/router'

import commerce from '@lib/api/commerce'
import { FAQ, Layout } from '@components/common'
import { getGlobalContentContext } from '@drf/getGlobalContentContext'
import { useGlobalContentContext } from '@context/GlobalContentContext'
import { PostView } from '@components/blog'
import { getPageContext } from '@drf/getPageContext'
import { LoreThreeDetailPage } from '@generated/model/loreThreeDetailPage'
import {
  DetailSection,
  Hero,
  ScanSections,
} from '@components/lorethreedetailpage'
import { Container, useUI } from '@components/ui'
import { Performance } from '@components/lorethreepage'
import { CTABar } from '@components/lorethreedetailpage/CTABar'

export async function getStaticProps({
  params,
  locale,
  locales,
  preview,
}: GetStaticPropsContext<{ slug: string }>) {
  const config = { locale, locales }

  const { product } = await commerce.getProduct({
    variables: { slug: 'loretwo' },
    config,
    preview,
  })

  const page: LoreThreeDetailPage = await getPageContext(
    'lorethreedetailpage/' + params?.slug
  )

  const homepage = await getPageContext('lorethreepage')

  if (!product) {
    throw new Error(`Product with slug _loreone-1_ not found`)
  }

  const global = await getGlobalContentContext()

  return {
    props: {
      page,
      global,
      product,
      homepage,
    },
    revalidate: 60,
  }
}

export async function getStaticPaths({ locales }: GetStaticPathsContext) {
  const config = { locales }

  const pages: LoreThreeDetailPage[] = await getPageContext(
    'lorethreedetailpage'
  )

  return {
    paths: Object.values(pages).map((page: any) => `/lorethree/${page.slug}`),
    fallback: 'blocking',
  }
}

export default function Page({
  page,
  global,
  product,
  homepage,
}: InferGetStaticPropsType<typeof getStaticProps>) {
  const router = useRouter()

  const { setData } = useGlobalContentContext()

  const { setPageTheme, setContainerFixedWidth } = useUI()

  useEffect(() => {
    setData(global)
  }, [global, setData])

  useEffect(() => {
    setContainerFixedWidth()
    setPageTheme('LIGHT')
  }, [])

  useEffect(() => {
    if (page?.color_scheme) {
      document.documentElement.style.setProperty(
        '--page-color',
        `var(--color-${page.color_scheme})`
      )
      if ((page.color_scheme as unknown as string) == 'green') {
        document.documentElement.style.setProperty(
          '--page-text-color',
          `var(--color-primary)`
        )
      } else {
        document.documentElement.style.setProperty(
          '--page-text-color',
          `var(--color-white)`
        )
      }
    }
  }, [])

  return router.isFallback ? (
    <h1>Loading...</h1>
  ) : (
    <>
      <CTABar product={product} page={page} />

      <Hero page={page} />
      {page.sections?.map((section) => (
        <DetailSection key={section.id} section={section} />
      ))}

      {page.scan_sections && <ScanSections sections={page.scan_sections} />}

      {homepage.performance_sections && (
        <Performance
          data={{
            sections: homepage.performance_sections,
            navigation_title: '',
          }}
        />
      )}

      {homepage.faqblock && product && (
        <Container>
          <FAQ
            title={homepage.faqblock.title}
            faqs={homepage.faqblock.faqs}
            expandAll={false}
          />
        </Container>
      )}
    </>
  )
}

Page.Layout = Layout
